import CustomComponent from '../CustomComponent';
import SelectorEngine from '../../utils/dom/selector-engine';

/**
 * YouTubeShorts Web Component
 * Displays thumbnail and title from passed-in params. No API call – playback in carousel modal.
 *
 * Usage:
 * <lta-youtube-shorts video-code="VIDEO_ID" title="Title" thumbnail="https://..."></lta-youtube-shorts>
 *
 * Props (all passed in; no API call):
 * - video-code: YouTube video ID (required)
 * - title: video title (default: "YouTube video player")
 * - thumbnail: thumbnail URL (default: YouTube default for video ID)
 * - width: card max-width (default: 315)
 * - height: aspect ratio height (default: 560)
 * - description, published-at, duration, video-url, embed-url: optional metadata (video-url/embed-url default from video-code for schema)
 */
export class YouTubeShortsComponent extends CustomComponent {
  static key = 'lta-youtube-shorts';

  constructor() {
    super();
    this.state = {};
  }

  static get observedAttributes() {
    return ['video-code', 'title', 'thumbnail', 'width', 'height', 'description', 'published-at', 'duration', 'video-url', 'embed-url'];
  }

  _getSchemaScript() {
    const {
      videoCode = '',
      title = 'YouTube video player',
      thumbnail = '',
      description = '',
      publishedAt = '',
      duration = '',
      videoUrl = '',
      embedUrl = ''
    } = this.props;

    const contentUrl = videoUrl && videoUrl.trim() ? videoUrl : `https://www.youtube.com/watch?v=${videoCode}`;
    const embedUrlResolved = embedUrl && embedUrl.trim() ? embedUrl : `https://www.youtube.com/embed/${videoCode}`;
    const thumbnailUrl = thumbnail && thumbnail.trim() ? thumbnail : `https://i.ytimg.com/vi/${videoCode}/maxresdefault.jpg`;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: title,
      thumbnailUrl,
      contentUrl,
      embedUrl: embedUrlResolved,
      author: { '@type': 'Organization', name: 'LTA - Lawn Tennis Association' },
      publisher: {
        '@type': 'Organization',
        name: 'LTA - Lawn Tennis Association',
        logo: { '@type': 'ImageObject', url: 'https://www.lta.org.uk/490e9f/siteassets/global/lta-general-use-logo.svg' }
      }
    };
    if (description && description.trim()) schema.description = description;
    if (publishedAt && publishedAt.trim()) schema.uploadDate = publishedAt;
    if (duration && duration.trim()) schema.duration = duration;

    return `<script type="application/ld+json">${JSON.stringify(schema)}</script>`;
  }

  render() {
    const {
      videoCode = '',
      title = 'YouTube video player',
      thumbnail = '',
      width = '315',
      height = '560'
    } = this.props;

    if (!videoCode) {
      return '<div class="lta-youtube-shorts__error">Please provide a YouTube video code.</div>';
    }

    const safeTitle = this._escapeHtml(title);
    const thumbnailUrl = thumbnail && thumbnail.trim() ? thumbnail : `https://i.ytimg.com/vi/${videoCode}/maxresdefault.jpg`;
    const schemaScript = this._getSchemaScript();

    return `
      <div class="lta-youtube-shorts lta-youtube-short-carousel__slide" role="group" aria-label="YouTube Short: ${safeTitle}">
        <div class="lta-youtube-short-carousel__thumbnail">
          <div class="lta-youtube-short-carousel__gradient"></div>
          <img src="${thumbnailUrl}" alt="" class="lta-youtube-shorts__thumbnail-img" data-video-code="${videoCode}" loading="lazy">
          <p class="lta-youtube-short-carousel__title lta-youtube-shorts__title">${safeTitle}</p>
          <div class="lta-youtube-shorts__play-button" aria-label="Play video in full screen: ${safeTitle}" data-video-code="${videoCode}" tabindex="0" role="button"></div>
        </div>
      </div>
      ${schemaScript}
    `;
  }

  afterRender() {
    const thumbnail = SelectorEngine.findOne('.lta-youtube-short-carousel__thumbnail', this);
    const playButton = SelectorEngine.findOne('.lta-youtube-shorts__play-button', this);
    if (thumbnail) {
      this._thumbnailHandler = this._handleOpenShort.bind(this);
      thumbnail.addEventListener('click', this._thumbnailHandler);
    }
    if (playButton) {
      this._playButtonKeyHandler = this._handlePlayButtonKeydown.bind(this);
      playButton.addEventListener('keydown', this._playButtonKeyHandler);
    }
  }

  onUnload() {
    const thumbnail = SelectorEngine.findOne('.lta-youtube-short-carousel__thumbnail', this);
    const playButton = SelectorEngine.findOne('.lta-youtube-shorts__play-button', this);
    if (thumbnail && this._thumbnailHandler) {
      thumbnail.removeEventListener('click', this._thumbnailHandler);
    }
    if (playButton && this._playButtonKeyHandler) {
      playButton.removeEventListener('keydown', this._playButtonKeyHandler);
    }
  }

  _handlePlayButtonKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault(); // Prevent Space from scrolling the page
    const playButton = SelectorEngine.findOne('.lta-youtube-shorts__play-button', this);
    if (playButton && document.activeElement === playButton) {
      this._handleOpenShort();
    }
  }

  _handleOpenShort() {
    const playButton = SelectorEngine.findOne('.lta-youtube-shorts__play-button', this);
    if (playButton) playButton.classList.add('is-playing');

    const videoCode = this.getAttribute('video-code');
    const title = this.getAttribute('title') || 'YouTube video player';
    const width = this.getAttribute('width') || '315';
    const height = this.getAttribute('height') || '560';

    this.dispatchEvent(new CustomEvent('youtube-short-play', {
      bubbles: true,
      detail: { videoCode, title, width, height, component: this }
    }));
  }

  _escapeHtml(str) {
    const div = typeof document !== 'undefined' ? document.createElement('div') : { textContent: '', innerHTML: '' };
    div.textContent = str;
    return div.innerHTML || str;
  }

}

export default YouTubeShortsComponent;
